import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  Checkbox,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  Tooltip,
  Box,
  IconButton,
  Stack,
} from "@mui/material";
import {
  ExpandMore,
  Add,
  Remove,
  FiberManualRecord,
} from "@mui/icons-material";
import {
  FuneralServiceAccordion,
  ProviderDetailsAccordion,
} from "../details-page.types";
import "./ServicesAccordion.scss";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getCartCount,
  createCart,
  getCartDetails,
  getCartSummary,
} from "../../../api/cart.api";
import { postContinueToOrder } from "../../../api/order-summary.api";
import { routes } from "../../../constants/routes";
import { useSetLoading } from "../../../common/hooks/appearance";

interface ServicesAccordionProps {
  providerDetails: ProviderDetailsAccordion;
}

const NEED_TYPE_MAP: Record<string, string> = {
  pre_need: "Preneed",
  at_need: "Immediate Need",
};

const parseBooleanFlag = (value: any) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalizedValue = value.trim().toLowerCase();
    if (normalizedValue === "true") return true;
    if (normalizedValue === "false") return false;
  }
  return undefined;
};

const resolveSingleSelectionFlag = (
  source: any,
  type: "subService" | "merchandise",
) => {
  const candidateKeys =
    type === "subService"
      ? [
          "singleSubServiceSelection",
          "isSingleSubServiceSelection",
          "isSubserviceSingleSelect",
          "isSubServiceSingleSelect",
          "subServiceSingleSelect",
          "singleSubService",
          "allowOnlyOneSubService",
          "allowSingleSubServiceSelection",
          "single_sub_service_selection",
          "is_single_sub_service_selection",
          "sub_service_single_select",
          "single_sub_service",
          "allow_only_one_sub_service",
        ]
      : [
          "singleMerchandiseSelection",
          "isSingleMerchandiseSelection",
          "isMerchandiseSingleSelect",
          "merchandiseSingleSelect",
          "singleMerchandise",
          "allowOnlyOneMerchandise",
          "allowSingleMerchandiseSelection",
          "single_merchandise_selection",
          "is_single_merchandise_selection",
          "merchandise_single_select",
          "single_merchandise",
          "allow_only_one_merchandise",
        ];

  const candidateSources = [
    source,
    source?.service,
    source?.providerService,
    source?.data,
  ];

  for (const candidateSource of candidateSources) {
    for (const key of candidateKeys) {
      const parsedValue = parseBooleanFlag(candidateSource?.[key]);
      if (parsedValue !== undefined) {
        return parsedValue;
      }
    }
  }

  return false;
};

export default function ServicesAccordion({
  providerDetails,
}: ServicesAccordionProps) {
  // STATES
  const [selectedItems, setSelectedItems] = useState<
    Record<number, Set<number>>
  >({});
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [selectedEmptyServices, setSelectedEmptyServices] = useState<
    Set<number>
  >(new Set());
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [accordionServices, setAccordionServices] = useState<
    FuneralServiceAccordion[]
  >([]);

  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "info" | "warning" | "error"
  >("success");
  const [cartPartners, setCartPartners] = useState<any>([]);
  // const timeoutRef = useRef<any>(null);

  // CONSTANTS
  const dispatch = useDispatch();

  // provider ID from URL
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isLoggedIn = useSelector((state: any) => state.session.isLoggedIn);
  const [isLoading, setLoading] = useState<boolean>(false);
  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  useEffect(() => {
    if (providerDetails?.services) {
      const activeServices = providerDetails.services.map((service) => {
        const filteredItems =
          service.partnerServiceItems?.filter((item: any) => item.isActive) ||
          [];
        return {
          ...service,
          singleSubServiceSelection: resolveSingleSelectionFlag(
            service,
            "subService",
          ),
          singleMerchandiseSelection: resolveSingleSelectionFlag(
            service,
            "merchandise",
          ),
          partnerServiceItems: filteredItems,
          totalServices:
            service.partnerServiceItems?.length > 0
              ? filteredItems.length
              : service.totalServices,
        };
      });
      setAccordionServices(activeServices);
    } else {
      setAccordionServices([]);
    }
  }, [providerDetails]);

  const normalizeItemType = (itemType?: string) => {
    const normalizedType = String(itemType || "").toLowerCase();

    if (normalizedType.includes("sub")) return "subService";
    if (normalizedType.includes("merch")) return "merchandise";

    return "other";
  };

  const isSingleSelectionEnabled = (
    service: FuneralServiceAccordion,
    itemType?: string,
  ) => {
    const normalizedType = normalizeItemType(itemType);

    if (normalizedType === "subService") {
      return !!service.singleSubServiceSelection;
    }

    if (normalizedType === "merchandise") {
      return !!service.singleMerchandiseSelection;
    }

    return false;
  };

  const hasExclusiveSelectionRule = (service: FuneralServiceAccordion) => {
    return (
      !!service.singleSubServiceSelection ||
      !!service.singleMerchandiseSelection
    );
  };

  const getSelectAllTargetSet = (service: FuneralServiceAccordion) => {
    const nextSet = new Set<number>();
    let subServicePicked = false;
    let merchandisePicked = false;

    service.partnerServiceItems.forEach((item) => {
      const normalizedType = normalizeItemType(item.itemType);

      if (normalizedType === "subService" && service.singleSubServiceSelection) {
        if (!subServicePicked) {
          nextSet.add(item.partnerServiceItemId);
          subServicePicked = true;
        }
        return;
      }

      if (
        normalizedType === "merchandise" &&
        service.singleMerchandiseSelection
      ) {
        if (!merchandisePicked) {
          nextSet.add(item.partnerServiceItemId);
          merchandisePicked = true;
        }
        return;
      }

      nextSet.add(item.partnerServiceItemId);
    });

    return nextSet;
  };

  const areSetsEqual = (left: Set<number>, right: Set<number>) => {
    if (left.size !== right.size) return false;

    return Array.from(left).every((value) => right.has(value));
  };

  const isAllServiceItemsSelected = (service: FuneralServiceAccordion) => {
    const selectedSet = selectedItems[service.id] || new Set<number>();
    const serviceItems = service.partnerServiceItems;

    if (serviceItems.length === 0) return false;
    if (selectedSet.size !== serviceItems.length) return false;

    return serviceItems.every((item) => selectedSet.has(item.partnerServiceItemId));
  };

  const isItemDisabled = (
    service: FuneralServiceAccordion,
    itemId: number,
    itemType?: string,
  ) => {
    if (!isSingleSelectionEnabled(service, itemType)) {
      return false;
    }

    const selectedSet = selectedItems[service.id] || new Set<number>();
    if (selectedSet.has(itemId)) {
      return false;
    }

    const normalizedType = normalizeItemType(itemType);

    return service.partnerServiceItems.some(
      (item) =>
        normalizeItemType(item.itemType) === normalizedType &&
        selectedSet.has(item.partnerServiceItemId),
    );
  };
  // HANDLERS
  const handleToggleSelectAll = (service: FuneralServiceAccordion) => {
    setSelectedItems((prev: Record<number, Set<number>>) => {
      const currentSelection = prev[service.id] || new Set<number>();
      const selectAllTarget = getSelectAllTargetSet(service);
      const isAllSelected = areSetsEqual(currentSelection, selectAllTarget);

      return {
        ...prev,
        [service.id]: isAllSelected ? new Set() : selectAllTarget,
      };
    });
  };

  const handleToggleItem = (
    service: FuneralServiceAccordion,
    itemId: number,
  ) => {
    setSelectedItems((prev: Record<number, Set<number>>) => {
      const currentSet = prev[service.id] || new Set<number>();
      const updatedSet = new Set(currentSet);

      const selectedItem = service.partnerServiceItems.find(
        (item) => item.partnerServiceItemId === itemId,
      );
      const selectedItemType = selectedItem?.itemType;

      if (updatedSet.has(itemId)) {
        updatedSet.delete(itemId);
      } else {
        if (isSingleSelectionEnabled(service, selectedItemType)) {
          const normalizedType = normalizeItemType(selectedItemType);

          service.partnerServiceItems.forEach((item) => {
            if (normalizeItemType(item.itemType) === normalizedType) {
              updatedSet.delete(item.partnerServiceItemId);
            }
          });
        }

        updatedSet.add(itemId);
      }

      return { ...prev, [service.id]: updatedSet };
    });
  };

  const getCartData = () => {
    const storedCartDetails = localStorage.getItem("cartDetails");
    if (!storedCartDetails) return;

    try {
      const cartDetails = JSON.parse(storedCartDetails);

      // restore only for same provider details page
      if (String(cartDetails?.id) !== String(id)) return;

      setSelectedItems(
        deserializeSelectedItems(cartDetails?.selectedItems || {}),
      );

      setExpandedIds(
        new Set(
          cartDetails?.expandedIds ? JSON.parse(cartDetails.expandedIds) : [],
        ),
      );

      setQuantities(cartDetails?.quantities || {});

      setSelectedEmptyServices(
        new Set(
          cartDetails?.selectedEmptyServices
            ? JSON.parse(cartDetails.selectedEmptyServices)
            : [],
        ),
      );
    } catch (error) {
      console.error("Error restoring cartDetails from localStorage:", error);
    }
  };
  //   const storedCartDetails = localStorage.getItem("cartDetails");
  //   if (!storedCartDetails) return;
  //   const cartDetails = JSON.parse(storedCartDetails);
  //   setSelectedItems(deserializeSelectedItems(cartDetails.selectedItems));
  //   setExpandedIds(
  //     new Set(
  //       cartDetails?.expandedIds ? JSON.parse(cartDetails?.expandedIds) : [],
  //     ),
  //   );

  //   setQuantities(cartDetails?.quantities);

  //   const cartServices = cartDetails?.accordionServices?.map((service: any) => {
  //     const filteredItems = service.partnerServiceItems?.filter((item: any) => item.isActive) || [];
  //     return {
  //       ...service,
  //       partnerServiceItems: filteredItems,
  //       totalServices: service.partnerServiceItems?.length > 0 ? filteredItems.length : service.totalServices,
  //     };
  //   });
  //   setAccordionServices(cartServices || []);

  //   setSelectedEmptyServices(
  //     new Set(
  //       cartDetails?.selectedEmptyServices
  //         ? JSON.parse(cartDetails?.selectedEmptyServices)
  //         : [],
  //     ),
  //   );
  //   if (timeoutRef.current) {
  //     clearTimeout(timeoutRef.current);
  //   }
  //   timeoutRef.current = setTimeout(() => {
  //     localStorage.removeItem("cartDetails");
  //   }, 500)
  // };

  const serializeSelectedItems = (items: Record<number, Set<number>>) => {
    const result: Record<number, number[]> = {};

    Object.keys(items).forEach((key) => {
      result[Number(key)] = Array.from(items[Number(key)]);
    });

    return result;
  };

  const deserializeSelectedItems = (
    items: Record<number, number[]>,
  ): Record<number, Set<number>> => {
    const result: Record<number, Set<number>> = {};

    Object.keys(items).forEach((key) => {
      result[Number(key)] = new Set(items[Number(key)]);
    });

    return result;
  };
  const handleAuthRequired = () => {
    const currentPath = window.location.pathname;

    const cardDetails = {
      path: currentPath,
      id,
      expandedIds: JSON.stringify(Array.from(expandedIds)),
      selectedEmptyServices: JSON.stringify(Array.from(selectedEmptyServices)),
      quantities,
      selectedItems: serializeSelectedItems(selectedItems),
    };

    dispatch({
      type: "CART:SET_CART_DETAILS",
      payload: cardDetails,
    });

    localStorage.setItem("cartDetails", JSON.stringify(cardDetails));
    localStorage.setItem("postLoginRedirect", currentPath);

    // setSnackbarSeverity("info");
    // setSnackbarMsg("Please log in to continue or add services");
    // setSnackbarOpen(true);

    navigate("/");
  };

  useEffect(() => {
    getCartData();
  }, [id]);

  const handleUpdateQuantity = (itemId: number, delta: number) => {
    setQuantities((prev: Record<number, number>) => {
      const currentQty = prev[itemId] || 1;
      const newQty = Math.max(1, currentQty + delta);
      return { ...prev, [itemId]: newQty };
    });
  };

  const isItemSelected = (serviceId: number, itemId: number) =>
    selectedItems[serviceId]?.has(itemId) ?? false;

  const accordionTotals = useMemo(() => {
    const totals: Record<number, number> = {};
    accordionServices.forEach((service) => {
      let total = 0;
      const selected = selectedItems[service.id];
      const items = service.partnerServiceItems;

      if (items.length === 0) {
        total = service.totalPrice ?? 0;
      } else if (selected && selected.size > 0) {
        items.forEach((item) => {
          if (selected.has(item.partnerServiceItemId)) {
            const qty = quantities[item.partnerServiceItemId] || 1;
            total += parseFloat(item.price) * qty;
          }
        });
      } else {
        items.forEach((item) => {
          const qty = quantities[item.partnerServiceItemId] || 1;
          total += parseFloat(item.price) * qty;
        });
      }
      totals[service.id] = total || 0;
    });
    return totals;
  }, [accordionServices, selectedItems, quantities]);

  const totalCost = useMemo(() => {
    let total = 0;
    accordionServices.forEach((service) => {
      const selected = selectedItems[service.id];
      if (selected) {
        selected.forEach((itemId) => {
          const item = service.partnerServiceItems.find(
            (i) => i.partnerServiceItemId === itemId,
          );
          if (item) {
            const qty = quantities[itemId] || 1;
            total += parseFloat(item.price) * qty;
          }
        });
      }
    });
    return total.toFixed(2);
  }, [selectedItems, accordionServices, quantities]);

  const anySelectedOrEmpty = useMemo(() => {
    return (
      Object.values(selectedItems).some((set) => set.size > 0) ||
      selectedEmptyServices.size > 0
    );
  }, [selectedItems, selectedEmptyServices]);

  const handleAccordionToggle = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleContinueToOrder = async () => {
    const selectedServices: {
      partnerServiceItemId: number;
      price: number;
      quantity: number;
    }[] = [];
    accordionServices.forEach((service) => {
      const selected = selectedItems[service.id];
      if (selected && selected.size > 0) {
        service.partnerServiceItems.forEach((item) => {
          if (selected.has(item.partnerServiceItemId)) {
            selectedServices.push({
              partnerServiceItemId: item.partnerServiceItemId,
              price: parseFloat(item.price),
              quantity: quantities[item.partnerServiceItemId] || 1,
            });
          }
        });
      }
    });

    if (selectedServices.length === 0) {
      console.warn("No services selected to continue");
      return;
    }

    //  API body
    const body = { items: selectedServices };

    try {
      const response = await postContinueToOrder(body);
      const countResponse = await getCartCount();
      setLoading(false);
      const cartCount = countResponse.data?.data?.data?.totalCount ?? 0;

      dispatch({
        type: "CART:SET_COUNT",
        payload: { count: cartCount },
      });

      if (response?.data.success) {
        // Navigate to order summary
        localStorage.removeItem("cartDetails");
        localStorage.removeItem("postLoginRedirect");
        navigate(`/order-summary`, {
          state: {
            orderId: response.data.data.data.order.orderId,
            partnerId: providerDetails.id,
          },
        });
      } else {
        console.error(
          "Failed to continue with selected services",
          response.data,
        );
      }
    } catch (error) {
      console.error("Error in handleContinue:", error);
    }
  };

  const fetchCartSummary = async () => {
    try {
      const response = await getCartSummary();
      setLoading(false);
      const apiData = response?.data?.data?.data;
      const partners = Array.isArray(apiData?.partnerItems?.partners)
        ? apiData?.partnerItems?.partners
        : [];
      if (partners.length > 0) {
        setCartPartners(partners);
      } else {
        setCartPartners([]);
      }
    } catch (error: any) {
      console.error("CART SUMMARY ERROR::", error);
      setCartPartners([]);
      setLoading(false);
    }
  };

  const checkOderFromAnotherProvider = () => {
    let isSamePartner = true;
    for (let index = 0; index < cartPartners?.length; index++) {
      if (
        String(cartPartners[index].partnerId) !== String(providerDetails?.id)
      ) {
        isSamePartner = false;
        break;
      }
    }
    if (!isSamePartner) {
      setSnackbarOpen(true);
      setSnackbarMsg(
        "You can place an order with only one provider/planner at a time. Please select a single provider/planner to proceed.",
      );
    }
    return isSamePartner;
  };
  const handleAddToCart = async () => {
    const selectedServices: {
      partnerServiceItemId: number;
      price: number;
      quantity: number;
    }[] = [];

    if (!checkOderFromAnotherProvider()) {
      return;
    }

    accordionServices.forEach((service) => {
      const selected = selectedItems[service.id];
      if (selected && selected.size > 0) {
        service.partnerServiceItems.forEach((item) => {
          if (selected.has(item.partnerServiceItemId)) {
            selectedServices.push({
              partnerServiceItemId: item.partnerServiceItemId,
              price: parseFloat(item.price),
              quantity: quantities[item.partnerServiceItemId] || 1,
            });
          }
        });
      }
    });

    if (selectedServices.length === 0) {
      console.warn("No items selected to add to cart");
      return;
    }

    // Check for duplicates
    const existingServiceIds = new Set();
    cartPartners.forEach((partner: any) => {
      partner.services.forEach((service: any) => {
        service.subServices.forEach((sub: any) => {
          existingServiceIds.add(sub.subServiceId);
        });
      });
    });

    const newServices = selectedServices.filter(
      (s) => !existingServiceIds.has(s.partnerServiceItemId),
    );

    if (newServices.length === 0) {
      setSnackbarSeverity("info");
      setSnackbarOpen(true);
      setSnackbarMsg("Selected services are already in your cart");
      return;
    }

    const body = { items: newServices };

    try {
      const response = await createCart(body);
      if (response.data.success) {
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setSnackbarMsg("Services added to your selected services successfully");
        // Update cart count immediately
        const countResponse = await getCartCount();
        const count = countResponse.data?.data?.data?.totalCount ?? 0;

        // Dispatch to Redux
        dispatch({ type: "CART:SET_COUNT", payload: { count: count } });
        localStorage.removeItem("cartDetails");
        localStorage.removeItem("postLoginRedirect");

        // Navigate to cart
        navigate("/cart", {
          state: {
            partnerId: providerDetails.id,
            isFromDetails: true,
          },
        });
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
    }
  };

  useEffect(() => {
    // Reset selections & expanded accordion when provider changes
    const storedCartDetails = localStorage.getItem("cartDetails");
    if (!storedCartDetails) {
      setSelectedItems({});
      setExpandedIds(new Set());
    }
  }, [id]);

  useEffect(() => {
    if (isLoggedIn) {
      fetchCartSummary();
    }
  }, [isLoggedIn]);

  if (!accordionServices || accordionServices.length === 0) {
    return (
      <div className="services-accordion">
        <Typography className="services-accordion__listOfServices">
          List of Services
        </Typography>

        <div className="services-accordion__empty">
          <Typography className="services-accordion__empty-text">
            No services available
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="services-accordion">
      <Typography className="services-accordion__listOfServices">
        List of Services
      </Typography>
      {accordionServices.map((service) => (
        <Accordion
          key={service.id}
          expanded={expandedIds.has(service.id)}
          onChange={() => handleAccordionToggle(service.id)}
          className="services-accordion__item"
        >
          <AccordionSummary
            className="services-accordion__summary"
            sx={{
              paddingLeft: isTablet ? "0.5rem !important" : "0 !important",
              paddingRight: isTablet ? "0.5rem !important" : "0 !important",
              "& .MuiAccordionSummary-content": {
                margin: isMobile ? "8px 0 !important" : "12px 0 !important",
              },
            }}
          >
            <div className="services-accordion__summary-content">
              {/* Main Section: Icon + Name + Price (Mobile) + Need Type (Desktop) */}
              <div className="services-accordion__summary-main">
                <ExpandMore
                  className={`services-accordion__custom-expand-icon ${
                    expandedIds.has(service.id) ? "expanded" : ""
                  }`}
                />

                <div className="services-accordion__summary-info">
                  <div className="services-accordion__name-row">
                    <Typography className="services-accordion__name">
                      {service.name}
                    </Typography>

                    {isMobile && (
                      <Typography className="services-accordion__mobile-price">
                        – ${accordionTotals[service.id]?.toFixed(0) ?? 0}
                      </Typography>
                    )}

                    {!isMobile && service.needType && (
                      <div className="services-accordion__desktop-need-type">
                        <FiberManualRecord
                          className="services-accordion__dot"
                          sx={{
                            fontSize: "8px",
                            color: "#104145",
                            marginLeft: "12px",
                            marginRight: "12px",
                          }}
                        />
                        <span className="services-accordion__need-type">
                          {NEED_TYPE_MAP[service.needType] || service.needType}
                        </span>
                      </div>
                    )}
                  </div>

                  {isMobile && (
                    <div className="services-accordion__mobile-bottom">
                      <span className="services-accordion__mobile-need-type-text">
                        {NEED_TYPE_MAP[service.needType] || service.needType}
                      </span>
                      <div className="services-accordion__mobile-button-wrapper">
                        <Button
                          size="small"
                          disabled={hasExclusiveSelectionRule(service)}
                          className={`services-accordion__select-btn ${
                            service.partnerServiceItems.length === 0
                              ? selectedEmptyServices?.has(service.id)
                                ? "selected"
                                : ""
                              : isAllServiceItemsSelected(service)
                                ? "selected"
                                : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (hasExclusiveSelectionRule(service)) return;
                            if (service.partnerServiceItems.length === 0) {
                              setSelectedEmptyServices((prev) => {
                                const updated = new Set(prev);
                                if (updated.has(service.id)) {
                                  updated.delete(service.id);
                                } else {
                                  updated.add(service.id);
                                }
                                return updated;
                              });
                            } else {
                              handleToggleSelectAll(service);
                            }
                          }}
                        >
                          {service.partnerServiceItems.length === 0
                            ? selectedEmptyServices.has(service.id)
                              ? "Selected"
                              : "Select"
                            : isAllServiceItemsSelected(service)
                              ? "Selected"
                              : "Select"}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Desktop Actions Section */}
              {!isMobile && (
                <div className="services-accordion__summary-actions">
                  <Typography className="services-accordion__desktop-price">
                    – ${accordionTotals[service.id]?.toFixed(0) ?? 0}
                  </Typography>

                  <Tooltip
                    title={
                      hasExclusiveSelectionRule(service)
                        ? "Allowed to select one sub-service and one merchandise item."
                        : ""
                    }
                    arrow
                    disableHoverListener={!hasExclusiveSelectionRule(service)}
                  >
                    <span
                      className="services-accordion__button-wrapper"
                      onClick={(e) => {
                        if (hasExclusiveSelectionRule(service)) {
                          e.stopPropagation();
                        }
                      }}
                      onMouseDown={(e) => {
                        if (hasExclusiveSelectionRule(service)) {
                          e.stopPropagation();
                        }
                      }}
                    >
                      <Button
                        size="small"
                        disabled={hasExclusiveSelectionRule(service)}
                        sx={{
                          cursor: hasExclusiveSelectionRule(service)
                            ? "not-allowed"
                            : "pointer",
                          "&.Mui-disabled": {
                            cursor: "not-allowed",
                            pointerEvents: "none",
                          },
                        }}
                        className={`services-accordion__select-btn ${
                          service.partnerServiceItems.length === 0
                            ? selectedEmptyServices?.has(service.id)
                              ? "selected"
                              : ""
                            : isAllServiceItemsSelected(service)
                              ? "selected"
                              : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (hasExclusiveSelectionRule(service)) return;
                          if (service.partnerServiceItems.length === 0) {
                            setSelectedEmptyServices((prev) => {
                              const updated = new Set(prev);
                              if (updated.has(service.id)) {
                                updated.delete(service.id);
                              } else {
                                updated.add(service.id);
                              }
                              return updated;
                            });
                          } else {
                            handleToggleSelectAll(service);
                          }
                        }}
                      >
                        {service.partnerServiceItems.length === 0
                          ? selectedEmptyServices.has(service.id)
                            ? "Selected"
                            : "Select"
                          : isAllServiceItemsSelected(service)
                            ? "Selected"
                            : "Select"}
                      </Button>
                    </span>
                  </Tooltip>
                </div>
              )}
            </div>
          </AccordionSummary>

          <AccordionDetails
            className="services-accordion__details"
            sx={{
              paddingLeft: isTablet ? "0.5rem !important" : "0 !important",
              paddingRight: isTablet ? "0.5rem !important" : "2.5rem !important",
              paddingBottom: 0,
              paddingTop: 0,
            }}
          >
            <Typography className="services-accordion__step-summary">
              <span className="services-accordion__step-summary--bold">
                Summary of Steps
              </span>
              {" - "}
              <span className="services-accordion__step-summary--normal">
                Customize as per your choice.
              </span>
            </Typography>

            {service.partnerServiceItems.map((item) => (
              <div
                className="services-accordion__service-row"
                key={item.partnerServiceItemId}
              >
                <div
                  className="services-accordion__left"
                  onClick={() => handleToggleItem(service, item.partnerServiceItemId)}
                >
                  <Checkbox
                    checked={isItemSelected(
                      service.id,
                      item.partnerServiceItemId,
                    )}
                    disabled={isItemDisabled(
                      service,
                      item.partnerServiceItemId,
                      item.itemType,
                    )}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => handleToggleItem(service, item.partnerServiceItemId)}
                    sx={{
                      color: "#104145",
                      "&.Mui-checked": {
                        color: "white",
                      },
                      "&.Mui-checked svg": {
                        fill: "#104145",
                      },
                    }}
                  />

                  <Tooltip title={item.name}>
                    <Typography className="services-accordion__item-name">
                      {item.name}
                    </Typography>
                  </Tooltip>
                </div>
                <Typography className="services-accordion__item-description">
                  {item.description}
                </Typography>

                <div className="services-accordion__right-actions">
                  {item.itemType === "merchandise" && (
                    <Box
                      className="services-accordion__quantity-selector"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconButton
                        size="small"
                        disabled={
                          (quantities[item.partnerServiceItemId] || 1) <= 1
                        }
                        onClick={() =>
                          handleUpdateQuantity(item.partnerServiceItemId, -1)
                        }
                        sx={{ color: "#104145" }}
                      >
                        <Remove fontSize="small" />
                      </IconButton>
                      <Typography
                        sx={{
                          minWidth: "24px",
                          textAlign: "center",
                          fontWeight: 600,
                        }}
                      >
                        {quantities[item.partnerServiceItemId] || 1}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleUpdateQuantity(item.partnerServiceItemId, 1)
                        }
                        sx={{ color: "#104145" }}
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </Box>
                  )}

                  <Typography className="services-accordion__item-price">
                    ${" "}
                    {(
                      parseFloat(item.price) *
                      (quantities[item.partnerServiceItemId] || 1)
                    ).toFixed(0)}
                  </Typography>
                </div>
              </div>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}

      {anySelectedOrEmpty && (
        <div className="services-accordion__summary-box">
          <Typography className="services-accordion__summary-title">
            Services Selected
          </Typography>
          <div className="services-accordion__summary-content">
            <div className="services-accordion__summary-list">
              {accordionServices.map((service) => {
                const selected = selectedItems[service.id];
                const isEmptyService = service.partnerServiceItems.length === 0;
                const isSelectedEmpty = selectedEmptyServices.has(service.id);

                if ((!selected || selected.size === 0) && !isSelectedEmpty)
                  return null;

                const isFull = selected?.size === service.totalServices;
                const isSelectAllSelection = areSetsEqual(
                  selected || new Set<number>(),
                  getSelectAllTargetSet(service),
                );

                return (
                  <div
                    key={service.id}
                    className="services-accordion__summary-pill"
                  >
                    {service.name} –{" "}
                    {isEmptyService
                      ? "Service Included"
                      : isFull && !hasExclusiveSelectionRule(service)
                        ? "All Items Included"
                        : isSelectAllSelection && hasExclusiveSelectionRule(service)
                          ? `${selected.size} item(s) selected`
                        : `${selected.size} item(s) selected`}
                  </div>
                );
              })}
            </div>

            <div className="services-accordion__summary-right">
              <Typography className="services-accordion__total">
                Total Cost –{" "}
                <span className="services-accordion__total-amount">
                  $ {totalCost.split(".")[0]}
                </span>
              </Typography>

              {totalCost !== "0.00" && (
                <>
                  <Tooltip
                    title={
                      !isLoggedIn
                        ? "Please login to continue with your order"
                        : ""
                    }
                    arrow
                    disableHoverListener={isLoggedIn}
                  >
                    <span>
                      <Button
                        className="services-accordion__btn services-accordion__btn--continue"
                        // disabled={!isLoggedIn}
                        onClick={
                          isLoggedIn
                            ? handleContinueToOrder
                            : handleAuthRequired
                        }
                      >
                        Continue
                      </Button>
                    </span>
                  </Tooltip>

                  <Tooltip
                    title={!isLoggedIn ? "Please login to add selection" : ""}
                    arrow
                    disableHoverListener={isLoggedIn}
                  >
                    <span>
                      <Button
                        className="services-accordion__btn services-accordion__btn--addToCart"
                        onClick={
                          isLoggedIn ? handleAddToCart : handleAuthRequired
                        }
                      >
                        Add Selection
                      </Button>
                    </span>
                  </Tooltip>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            "& .MuiAlert-message": {
              padding: "8px 0",
            },
            "& .MuiAlert-icon": {
              display: "flex",
              alignItems: "center",
              padding: 0,
            },
            "& .MuiAlert-action": {
              display: "flex",
              alignItems: "center",
              padding: 0,
            },
          }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </div>
  );
}
